import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Clock, CalendarDays } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";

export default function AdminInternAnalytics() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const pager = usePager(items, 25);

  useEffect(() => {
    api.get("/admin/intern/analytics").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  const fmtMinutes = (m) => {
    if (!m) return "0 min";
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60); const r = m % 60;
    return `${h}h ${r}m`;
  };

  return (
    <div data-testid="admin-intern-analytics-page">
      <div className="flex items-start gap-3">
        <Users className="w-7 h-7 text-kb-primary mt-1" strokeWidth={1.5} />
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE PROGRAM</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Employee <em className="italic text-kb-primary">analytics</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Platform usage, daily-log cadence, and clock-in history for every employee.</p>
        </div>
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No employees yet.</div>
        ) : (
          <>
            <div className="mt-8 border border-slate-200 rounded-sm bg-white overflow-hidden">
              <div className="grid grid-cols-12 px-4 lg:px-6 py-3 border-b border-slate-200 bg-slate-50 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                <div className="col-span-3">Employee</div>
                <div className="col-span-2">Joined</div>
                <div className="col-span-1">Logs</div>
                <div className="col-span-2"><Clock className="w-3 h-3 inline" /> Time online</div>
                <div className="col-span-1"><CalendarDays className="w-3 h-3 inline" /> Active 30d</div>
                <div className="col-span-3">Last log / clock</div>
              </div>
              {pager.pageItems.map((i) => (
                <div key={i.user_id} className="grid grid-cols-12 px-4 lg:px-6 py-3 border-b border-slate-200 last:border-0 items-start text-sm" data-testid={`intern-row-${i.user_id}`}>
                  <div className="col-span-12 lg:col-span-3 min-w-0">
                    <div className="font-medium text-kb-ink truncate flex items-center gap-2">
                      {i.name || "—"}
                      {i.is_intern && <span className="kb-mono text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">Intern</span>}
                    </div>
                    <div className="kb-mono text-[10px] text-kb-ink-muted truncate">{i.email}{i.employee_role ? ` · ${i.employee_role}` : ""}</div>
                  </div>
                  <div className="col-span-6 lg:col-span-2 kb-mono text-[11px] text-kb-ink-muted">
                    {i.joined_at ? new Date(i.joined_at).toLocaleDateString() : "—"}
                  </div>
                  <div className="col-span-6 lg:col-span-1 font-mono text-kb-primary">{i.log_count}</div>
                  <div className="col-span-6 lg:col-span-2 font-mono">{fmtMinutes(i.minutes_online)}</div>
                  <div className="col-span-6 lg:col-span-1 font-mono">{i.active_days_30d}</div>
                  <div className="col-span-12 lg:col-span-3 kb-mono text-[11px] text-kb-ink-muted">
                    {i.last_log ? <div>Log {i.last_log.date}: <span className="text-kb-ink">{(i.last_log.summary || "").slice(0, 40)}{(i.last_log.summary || "").length > 40 ? "…" : ""}</span></div> : <div>No logs yet</div>}
                    {i.last_clock && <div>{i.last_clock.action === "in" ? "In" : "Out"} · {new Date(i.last_clock.at).toLocaleString()}</div>}
                  </div>
                </div>
              ))}
            </div>
            <Pager {...pager} label="interns" testId="admin-interns-pager" />
          </>
        )}
    </div>
  );
}
